import BalancePlanChip from './BalancePlanChip';
import { useConsentStore } from '../../store/consentStore';

/**
 * HomeStatusBar — абсолютно позиционированная верхняя зона Home (318:3124).
 *
 * Layout (figma-spec §2, §5 + strict-audit §3):
 *  - fixed top-0, высота 96dp + safe-area-inset-top
 *  - shine ellipse 104×104 behind the balance-plan chip (blur 170px)
 *  - BalancePlanChip centered horizontally, clickable
 *
 * Strict-audit §3 closures (all 3 deviations):
 *  - Shine opacity 0.65 → 1.0 (Figma fill a=1.0, 0.65 was INVENTED)
 *  - Shine position centered → left:31px from statusbar left edge
 *    (Figma shine ellipse coordinates: x=31, not at horizontal center)
 *  - Scrim gradient: Figma fills[0] = GRADIENT_LINEAR black a=1 top → a=0
 *    bottom. В первой итерации strict-audit ошибочно прочитал fills:[] на
 *    внешнем frame вместо statusbar — scrim должен быть, с точными Figma
 *    stops (bg-app #010103 0% → 1.0 opacity, bottom 0% opacity).
 *
 * В Telegram WebApp v6+ системные иконки (battery/wifi/time) рисует сам
 * Telegram, поэтому мы их не добавляем — соответствует Figma (visible:false).
 */
export default function HomeStatusBar({ credits = 0, planLabel = 'Free', onProfileClick }) {
  const consentRequired = useConsentStore((state) => state.consentRequired);
  if (consentRequired !== false) return null;

  return (
    <div
      className="fixed top-0 left-0 right-0 z-30 pointer-events-none"
      style={{
        // Safe-top в Telegram WebApp fullscreen уже покрывает системный status-bar
        // (~44dp на iOS, 24-32dp на Android). Figma statusbar height=96 — это
        // декоративная зона 96-22 = 74px над chip'ом. В нашем runtime safe-top
        // уже занимает системную область, поэтому additional top-pad = 12px
        // (воздух от системного бара до chip). Без hardcoded 96px чтобы
        // убрать "большой отступ сверху".
        paddingTop: 'calc(var(--safe-top, 0px) + 12px)',
        paddingBottom: '12px',
      }}
    >
      {/* Scrim gradient — per user spec 2026-04-19: pure black #000 top fade.
          Простой linear 0%→100% (без задержки до 40%) — воздух под
          статусбаром чище, hero сразу просвечивает ниже chip'а. */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            'linear-gradient(180deg, #000 0%, rgba(0, 0, 0, 0.00) 100%)',
        }}
      />

      {/* hero-shine glow — Figma ellipse @ (31, -73) relative to statusbar,
          fill icon/accent (#FFAC0B) α=1.0, LAYER_BLUR 170.67px. */}
      <div
        data-testid="home-statusbar-shine"
        aria-hidden
        className="absolute top-0 left-[31px] w-[104px] h-[104px] rounded-full"
        style={{
          background: '#FFAC0B',
          filter: 'blur(170px)',
        }}
      />

      {/* balance-plan capsule centered horizontally below Telegram chrome. */}
      <div className="relative mt-[41px] flex items-start justify-center pointer-events-auto">
        <BalancePlanChip
          credits={credits}
          planLabel={planLabel}
          onClick={onProfileClick}
        />
      </div>
    </div>
  );
}
